import { isFunction } from 'lodash/fp';
import type {
    Unkind,
    ReduxActionOutcome,
    ReduxApiAction,
    ReduxNonApiAction
} from 'app/store/storeTypes';
import { isJobManagementPage, isUsageReportingPage } from 'app/routes';
import { doGetUsageJobDefinitions } from 'app/store/usageReporting/actions.js';
import {
    CANCEL_JOB_RUN,
    DELETE_JOB_DEFINITION,
    DELETE_JOB_ENVIRONMENT,
    DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER,
    GET_JOB_ACTIVITY_METRICS,
    GET_JOB_RUN,
    GET_JOB_RUN_FILTER_OPTIONS,
    GET_JOB_RUN_TIMELINE,
    GET_JOB_TRIGGER_CANDIDATES_BY_ROLE,
    GET_LAST_JOB_RUN,
    GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS,
    GET_SNOWFLAKE_STAGE_DETAILS,
    GET_SNOWFLAKE_STAGE_FILE_INFO,
    type GetJobActivityMetrics,
    type GetJobRunFilterOptions,
    type GetJobRunTimeline,
    type GetSnowflakeStageFileInfo,
    type JobRunRequest,
    LIST_JOB_DEFINITIONS,
    LIST_JOB_ENVIRONMENTS,
    LIST_JOB_RUNS,
    LIST_SNOWFLAKE_FILE_FORMATS,
    LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS,
    LIST_SNOWFLAKE_STAGES,
    LIST_SNOWFLAKE_STAGE_FILES,
    type ListJobRuns,
    PAUSE_JOB_TRIGGER,
    RESUME_JOB_TRIGGER,
    SET_JOB_DEFINITION,
    SET_JOB_ENVIRONMENT,
    SET_SNOWFLAKE_SERVICE_ACCOUNT_USER,
    type SetJobDefinition,
    type SetJobEnvironment,
    type SetSnowflakeServiceAccountUser,
    type SnowflakeStageFqn,
    TRIGGER_JOB_RUN,
    TRIGGER_JOB_RUNS,
    type ListSnowflakeStoredProcedures,
    LIST_SNOWFLAKE_STORED_PROCEDURES,
    EXECUTE_QUERY_BY_ENVIRONMENT,
    AUDIT_JOB_RUN,
    type SetJobEnvironmentSuccess,
    type SetSnowflakeServiceAccountUserSuccess,
    type GetModelTagCheckCandidates,
    GET_MODEL_TAG_CHECK_CANDIDATES,
    GET_JOB_DEFINITION,
    type GetModelTagSourceTables,
    GET_MODEL_TAG_SOURCE_TABLES,
    type TargetJob
} from 'app/lib/types/server';
import type {
    CancelJobRunTypeMap,
    DeleteJobDefinitionTypeMap,
    DeleteJobEnvironmentTypeMap,
    DeleteSnowflakeServiceAccountUserTypeMap,
    ExecuteQueryByEnvironmentTypeMap,
    GetJobActivityMetricsTypeMap,
    GetJobRunFilterOptionsTypeMap,
    GetJobRunTimelineTypeMap,
    GetJobRunTypeMap,
    GetJobTriggerCandidatesByRoleTypeMap,
    GetLastJobRunTypeMap,
    GetProjectsContainingTableDeploymentsTypeMap,
    GetSnowflakeStageDetailsTypeMap,
    GetSnowflakeStageFileInfoTypeMap,
    ListJobDefinitionsTypeMap,
    ListJobEnvironmentsTypeMap,
    ListJobRunsTypeMap,
    ListSnowflakeFileFormatsTypeMap,
    ListSnowflakeServiceAccountUsersTypeMap,
    ListSnowflakeStageFilesTypeMap,
    ListSnowflakeStagesTypeMap,
    ListSnowflakeStoredProceduresTypeMap,
    PauseJobTriggerTypeMap,
    ResumeJobTriggerTypeMap,
    SetJobDefinitionTypeMap,
    SetJobEnvironmentTypeMap,
    SetSnowflakeServiceAccountUserTypeMap,
    TriggerJobRunTypeMap,
    TriggerJobRunsTypeMap,
    AuditJobRunTypeMap,
    GetModelTagCheckCandidatesTypeMap,
    GetJobDefinitionTypeMap,
    GetModelTagSourceTablesTypeMap
} from 'app/lib/types/cqrs';
import {
    CANCEL_JOB_RUN_ACTION_TYPE,
    DELETE_JOB_DEFINITION_ACTION_TYPE,
    DELETE_JOB_ENVIRONMENT_ACTION_TYPE,
    DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
    GET_JOB_ACTIVITY_METRICS_ACTION_TYPE,
    GET_JOB_RUN_ACTION_TYPE,
    GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE,
    GET_JOB_RUN_TIMELINE_ACTION_TYPE,
    GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE,
    GET_LAST_JOB_RUN_ACTION_TYPE,
    GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS_ACTION_TYPE,
    GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE,
    GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE,
    LIST_JOB_DEFINITIONS_ACTION_TYPE,
    LIST_JOB_ENVIRONMENTS_ACTION_TYPE,
    LIST_JOB_RUNS_ACTION_TYPE,
    LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE,
    LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE,
    LIST_SNOWFLAKE_STAGES_ACTION_TYPE,
    LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE,
    PAUSE_JOB_TRIGGER_ACTION_TYPE,
    RESET_SET_ENVIRONMENT_ACTION_TYPE,
    RESET_SET_JOB_DEFINITION_ACTION_TYPE,
    RESET_SET_SERVICE_ACCOUNT_ACTION_TYPE,
    RESUME_JOB_TRIGGER_ACTION_TYPE,
    SET_JOB_DEFINITION_ACTION_TYPE,
    SET_JOB_ENVIRONMENT_ACTION_TYPE,
    SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
    TRIGGER_JOB_RUN_ACTION_TYPE,
    TRIGGER_JOB_RUNS_ACTION_TYPE,
    LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE,
    CLEAR_SNOWFLAKE_STAGE_FILES_LIST_ACTION_TYPE,
    LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE,
    GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE,
    GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE,
    CLEAR_MODEL_TAG_CHECK_CANDIDATES,
    GET_JOB_DEFINITION_ACTION_TYPE,
    GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE,
    CLEAR_MODEL_TAG_SOURCE_TABLES,
    SET_JOB_TRIGGER_CANDIDATES_FILTER,
    SET_JOB_DEFINITION_FILTER
} from './types';

export const doGetAuditData = (jobRunUid: string, environmentUid: string) =>
    ({
        type: GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE,
        api: {
            version: 2,
            name: AUDIT_JOB_RUN
        },
        params: {
            _kind: AUDIT_JOB_RUN,
            jobRunUid,
            environmentUid
        }
    }) as const satisfies ReduxApiAction<AuditJobRunTypeMap>;

export const doListProjects = () =>
    ({
        type: GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS_ACTION_TYPE,
        api: { version: 2, name: GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS }
    }) as const satisfies ReduxApiAction<GetProjectsContainingTableDeploymentsTypeMap>;

export const doListJobActivityMetrics = (params: GetJobActivityMetrics) =>
    ({
        type: GET_JOB_ACTIVITY_METRICS_ACTION_TYPE,
        api: { version: 2, name: GET_JOB_ACTIVITY_METRICS },
        params
    }) as const satisfies ReduxApiAction<GetJobActivityMetricsTypeMap>;

export const doListJobEnvironments = () =>
    ({
        type: LIST_JOB_ENVIRONMENTS_ACTION_TYPE,
        api: { version: 2, name: LIST_JOB_ENVIRONMENTS }
    }) as const satisfies ReduxApiAction<ListJobEnvironmentsTypeMap>;

export const doListDatabasesForEnvironment = (environmentUid: string) =>
    ({
        type: LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE,
        api: { version: 2, name: EXECUTE_QUERY_BY_ENVIRONMENT },
        params: {
            _kind: EXECUTE_QUERY_BY_ENVIRONMENT,
            environmentUid,
            sql: 'SHOW DATABASES'
        }
    }) as const satisfies ReduxApiAction<ExecuteQueryByEnvironmentTypeMap>;

export const doListJobTriggerCandidatesByRole = (targetJob: TargetJob) =>
    ({
        type: GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE,
        api: { version: 2, name: GET_JOB_TRIGGER_CANDIDATES_BY_ROLE },
        params: {
            _kind: GET_JOB_TRIGGER_CANDIDATES_BY_ROLE,
            targetJob
        }
    }) as const satisfies ReduxApiAction<GetJobTriggerCandidatesByRoleTypeMap>;

export interface Entry {
    type: string;
    value: string;
    name: string;
}

export const doSetJobTriggerCandidatesFilter = (param: Entry) =>
    ({
        type: SET_JOB_TRIGGER_CANDIDATES_FILTER,
        payload: param
    }) as const satisfies ReduxNonApiAction;

export const doSetJobDefinitionFilter = (param: Entry[]) =>
    ({
        type: SET_JOB_DEFINITION_FILTER,
        payload: param
    }) as const satisfies ReduxNonApiAction;
export const doListStoredProcedureCandidates = (params: ListSnowflakeStoredProcedures) =>
    ({
        type: LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE,
        api: { version: 2, name: LIST_SNOWFLAKE_STORED_PROCEDURES },
        params
    }) as const satisfies ReduxApiAction<ListSnowflakeStoredProceduresTypeMap>;

export const doListFilters = (params: GetJobRunFilterOptions) =>
    ({
        type: GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE,
        api: { version: 2, name: GET_JOB_RUN_FILTER_OPTIONS },
        params
    }) as const satisfies ReduxApiAction<GetJobRunFilterOptionsTypeMap>;

export const doListJobDefinitions = (onSuccess?: () => void) =>
    ({
        type: LIST_JOB_DEFINITIONS_ACTION_TYPE,
        api: { version: 2, name: LIST_JOB_DEFINITIONS },
        onSucceeded: () => {
            if (isFunction(onSuccess)) {
                onSuccess();
            }
        }
    }) as const satisfies ReduxApiAction<ListJobDefinitionsTypeMap>;

export const doListServiceAccounts = () =>
    ({
        type: LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE,
        api: { version: 2, name: LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS }
    }) as const satisfies ReduxApiAction<ListSnowflakeServiceAccountUsersTypeMap>;

export const doDeleteJobDefinition = (uid: string, name: string) =>
    ({
        type: DELETE_JOB_DEFINITION_ACTION_TYPE,
        api: { version: 2, name: DELETE_JOB_DEFINITION },
        params: { _kind: DELETE_JOB_DEFINITION, uid },
        meta: { name },
        onSucceeded: ({ dispatch }) => {
            if (isUsageReportingPage()) {
                dispatch(doGetUsageJobDefinitions());
            } else {
                dispatch(doListJobDefinitions());
            }
        }
    }) as const satisfies ReduxApiAction<DeleteJobDefinitionTypeMap>;

export const doDeleteJobEnvironment = (uid: string, name: string) =>
    ({
        type: DELETE_JOB_ENVIRONMENT_ACTION_TYPE,
        api: { version: 2, name: DELETE_JOB_ENVIRONMENT },
        params: { _kind: DELETE_JOB_ENVIRONMENT, uid },
        meta: { name }
    }) as const satisfies ReduxApiAction<DeleteJobEnvironmentTypeMap>;

export const doDeleteServiceAccount = (uid: string, name: string) =>
    ({
        type: DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
        api: { version: 2, name: DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER },
        params: { _kind: DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER, uid },
        meta: { name }
    }) as const satisfies ReduxApiAction<DeleteSnowflakeServiceAccountUserTypeMap>;

export const doListJobRuns = (params: Unkind<ListJobRuns>) =>
    ({
        type: LIST_JOB_RUNS_ACTION_TYPE,
        api: { version: 2, name: LIST_JOB_RUNS },
        params: {
            _kind: LIST_JOB_RUNS,
            ...params
        }
    }) as const satisfies ReduxApiAction<ListJobRunsTypeMap>;

export const doGetJobRun = (jobRunUid: string) =>
    ({
        type: GET_JOB_RUN_ACTION_TYPE,
        api: { version: 2, name: GET_JOB_RUN },
        params: {
            _kind: GET_JOB_RUN,
            jobRunUid
        }
    }) as const satisfies ReduxApiAction<GetJobRunTypeMap>;

export const doGetJobRunTimeline = (params: Unkind<GetJobRunTimeline>) =>
    ({
        type: GET_JOB_RUN_TIMELINE_ACTION_TYPE,
        api: { version: 2, name: GET_JOB_RUN_TIMELINE },
        params: {
            _kind: GET_JOB_RUN_TIMELINE,
            ...params
        }
    }) as const satisfies ReduxApiAction<GetJobRunTimelineTypeMap>;

export const doCancelJobRun = (jobRunUid: string) =>
    ({
        type: CANCEL_JOB_RUN_ACTION_TYPE,
        api: { version: 2, name: CANCEL_JOB_RUN },
        params: {
            _kind: CANCEL_JOB_RUN,
            jobRunUid
        },
        meta: {
            jobRunUid
        },
        onSucceeded: ({ dispatch, getState }) => {
            const { activityMetrics, jobRunFilters, jobRunTimeline } = getState().jobs;

            if (jobRunFilters) {
                dispatch(doListJobRuns(jobRunFilters));
            }

            if (activityMetrics?.params) {
                dispatch(doListJobActivityMetrics(activityMetrics.params));
            }

            if (jobRunTimeline?.params) {
                dispatch(doGetJobRunTimeline(jobRunTimeline.params));
            }
        }
    }) as const satisfies ReduxApiAction<CancelJobRunTypeMap>;

export const doSetEnvironment = (
    params: Unkind<SetJobEnvironment>,
    onSuccess?: (payload: SetJobEnvironmentSuccess) => void
) =>
    ({
        type: SET_JOB_ENVIRONMENT_ACTION_TYPE,
        api: { version: 2, name: SET_JOB_ENVIRONMENT },
        params: {
            _kind: SET_JOB_ENVIRONMENT,
            ...params
        },
        onSucceeded: ({ dispatch, payload }) => {
            if (isFunction(onSuccess)) {
                onSuccess(payload);
            }
            dispatch(doListJobEnvironments());
        }
    }) as const satisfies ReduxApiAction<SetJobEnvironmentTypeMap>;

export const doResetSetEnvironment = () =>
    ({
        type: RESET_SET_ENVIRONMENT_ACTION_TYPE
    }) as const satisfies ReduxNonApiAction;

export const doGetJobDefinition = (definitionUid: string) => ({
    type: GET_JOB_DEFINITION_ACTION_TYPE,
    api: { version: 2, name: GET_JOB_DEFINITION },
    meta: { definitionUid },
    params: {
        _kind: GET_JOB_DEFINITION,
        definitionUid
    }
});

export const doTriggerJobRun = (uid: string, name: string, skipChecks = false) =>
    ({
        type: TRIGGER_JOB_RUN_ACTION_TYPE,
        api: { version: 2, name: TRIGGER_JOB_RUN },
        params: { _kind: TRIGGER_JOB_RUN, definitionUid: uid, skipChecks },
        meta: { name }
    }) as const satisfies ReduxApiAction<TriggerJobRunTypeMap>;

export const doTriggerJobRuns = (jobs: JobRunRequest[]) =>
    ({
        type: TRIGGER_JOB_RUNS_ACTION_TYPE,
        api: { version: 2, name: TRIGGER_JOB_RUNS },
        params: { _kind: TRIGGER_JOB_RUNS, requests: jobs }
    }) as const satisfies ReduxApiAction<TriggerJobRunsTypeMap>;

export const doSetJobDefinition = (params: Unkind<SetJobDefinition>, onSuccess?: () => void) =>
    ({
        type: SET_JOB_DEFINITION_ACTION_TYPE,
        api: { version: 2, name: SET_JOB_DEFINITION },
        params: {
            _kind: SET_JOB_DEFINITION,
            ...params
        },
        onSucceeded: ({ dispatch, payload }) => {
            if (payload?.isJobRunRequired && params.uid) {
                dispatch(doTriggerJobRun(params.uid, params.name));
            }
            if (isFunction(onSuccess)) {
                onSuccess();
            }
            if (params.uid) {
                dispatch(doGetJobDefinition(params.uid));
            }
            if (isJobManagementPage()) {
                dispatch(doListJobDefinitions());
            }
            if (isUsageReportingPage()) {
                dispatch(doGetUsageJobDefinitions());
            }
        }
    }) as const satisfies ReduxApiAction<SetJobDefinitionTypeMap>;

export const doResetSetJobDefinition = () =>
    ({
        type: RESET_SET_JOB_DEFINITION_ACTION_TYPE
    }) as const satisfies ReduxNonApiAction;

export const doPauseJobSchedule = (uid: string) =>
    ({
        type: PAUSE_JOB_TRIGGER_ACTION_TYPE,
        api: { version: 2, name: PAUSE_JOB_TRIGGER },
        params: { _kind: PAUSE_JOB_TRIGGER, uid }
    }) as const satisfies ReduxApiAction<PauseJobTriggerTypeMap>;

export const doResumeJobSchedule = (uid: string) =>
    ({
        type: RESUME_JOB_TRIGGER_ACTION_TYPE,
        api: { version: 2, name: RESUME_JOB_TRIGGER },
        params: { _kind: RESUME_JOB_TRIGGER, uid }
    }) as const satisfies ReduxApiAction<ResumeJobTriggerTypeMap>;

export const doSetServiceAccount = (
    params: Unkind<SetSnowflakeServiceAccountUser>,
    onSuccess?: (payload: SetSnowflakeServiceAccountUserSuccess) => void
) =>
    ({
        type: SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
        api: { version: 2, name: SET_SNOWFLAKE_SERVICE_ACCOUNT_USER },
        params: {
            _kind: SET_SNOWFLAKE_SERVICE_ACCOUNT_USER,
            ...params
        },
        onSucceeded: ({ dispatch, payload }) => {
            if (isFunction(onSuccess)) {
                onSuccess(payload);
            }
            dispatch(doListServiceAccounts());
        }
    }) as const satisfies ReduxApiAction<SetSnowflakeServiceAccountUserTypeMap>;

export const doResetSetServiceAccount = () =>
    ({
        type: RESET_SET_SERVICE_ACCOUNT_ACTION_TYPE
    }) as const satisfies ReduxNonApiAction;

export const doListSnowflakeStages = (environmentUid: string) =>
    ({
        type: LIST_SNOWFLAKE_STAGES_ACTION_TYPE,
        api: { version: 2, name: LIST_SNOWFLAKE_STAGES },
        params: { _kind: LIST_SNOWFLAKE_STAGES, environmentUid }
    }) as const satisfies ReduxApiAction<ListSnowflakeStagesTypeMap>;

export const doGetSnowflakeStageDetails = (stage: SnowflakeStageFqn, environmentUid: string) =>
    ({
        type: GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE,
        api: { version: 2, name: GET_SNOWFLAKE_STAGE_DETAILS },
        params: { _kind: GET_SNOWFLAKE_STAGE_DETAILS, stage, environmentUid }
    }) as const satisfies ReduxApiAction<GetSnowflakeStageDetailsTypeMap>;

export const doListSnowflakeFileFormats = (environmentUid: string, isUnloadJob: boolean = false) =>
    ({
        type: LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE,
        api: { version: 2, name: LIST_SNOWFLAKE_FILE_FORMATS },
        params: {
            _kind: LIST_SNOWFLAKE_FILE_FORMATS,
            environmentUid,
            supportedForUnloading: isUnloadJob
        }
    }) as const satisfies ReduxApiAction<ListSnowflakeFileFormatsTypeMap>;

export const doListSnowflakeStageFiles = (
    environmentUid: string,
    stage: SnowflakeStageFqn,
    pattern?: string
) =>
    ({
        type: LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE,
        api: { version: 2, name: LIST_SNOWFLAKE_STAGE_FILES },
        params: {
            _kind: LIST_SNOWFLAKE_STAGE_FILES,
            environmentUid,
            stage,
            pattern
        }
    }) as const satisfies ReduxApiAction<ListSnowflakeStageFilesTypeMap>;

export const doClearSnowflakeStageFiles = () =>
    ({
        type: CLEAR_SNOWFLAKE_STAGE_FILES_LIST_ACTION_TYPE
    }) as const satisfies ReduxNonApiAction;

export const doGetSnowflakeStageFileInfo = ({
    environmentUid,
    fileFormat,
    stage,
    targetSchema,
    pattern,
    fileFormatParameters
}: GetSnowflakeStageFileInfo) =>
    ({
        type: GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE,
        api: { version: 2, name: GET_SNOWFLAKE_STAGE_FILE_INFO },
        params: {
            _kind: GET_SNOWFLAKE_STAGE_FILE_INFO,
            environmentUid,
            fileFormat,
            pattern,
            rawDataSize: 10000,
            stage,
            targetSchema,
            fileFormatParameters
        }
    }) as const satisfies ReduxApiAction<GetSnowflakeStageFileInfoTypeMap>;

export const doGetModelTagCheckCandidates = ({
    tagId,
    environmentUid,
    sourceTableMappings
}: GetModelTagCheckCandidates) =>
    ({
        type: GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE,
        api: { version: 2, name: GET_MODEL_TAG_CHECK_CANDIDATES },
        params: {
            _kind: GET_MODEL_TAG_CHECK_CANDIDATES,
            tagId,
            environmentUid,
            sourceTableMappings
        }
    }) as const satisfies ReduxApiAction<GetModelTagCheckCandidatesTypeMap>;

export const doClearModelTagCheckCandidates = () =>
    ({
        type: CLEAR_MODEL_TAG_CHECK_CANDIDATES
    }) as const satisfies ReduxNonApiAction;

export const doGetModelTagSourceTables = ({ tagId }: GetModelTagSourceTables) =>
    ({
        type: GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE,
        api: { version: 2, name: GET_MODEL_TAG_SOURCE_TABLES },
        params: {
            _kind: GET_MODEL_TAG_SOURCE_TABLES,
            tagId
        }
    }) as const satisfies ReduxApiAction<GetModelTagSourceTablesTypeMap>;

export const doClearModelTagSourceTables = () =>
    ({
        type: CLEAR_MODEL_TAG_SOURCE_TABLES
    }) as const satisfies ReduxNonApiAction;

export const doGetLastJobRun = (definitionUid: string) =>
    ({
        type: GET_LAST_JOB_RUN_ACTION_TYPE,
        api: { version: 2, name: GET_LAST_JOB_RUN },
        params: {
            _kind: GET_LAST_JOB_RUN,
            definitionUid
        }
    }) as const satisfies ReduxApiAction<GetLastJobRunTypeMap>;

export type JobActionOutcomes =
    | ReduxActionOutcome<
          ReturnType<typeof doListProjects>,
          GetProjectsContainingTableDeploymentsTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doGetAuditData>, AuditJobRunTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doListJobActivityMetrics>, GetJobActivityMetricsTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doListJobEnvironments>, ListJobEnvironmentsTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doListDatabasesForEnvironment>,
          ExecuteQueryByEnvironmentTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doListJobDefinitions>, ListJobDefinitionsTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doListFilters>, GetJobRunFilterOptionsTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doGetJobRun>, GetJobRunTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doListJobTriggerCandidatesByRole>,
          GetJobTriggerCandidatesByRoleTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doDeleteJobDefinition>, DeleteJobDefinitionTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doListServiceAccounts>,
          ListSnowflakeServiceAccountUsersTypeMap
      >
    | ReduxActionOutcome<
          ReturnType<typeof doListStoredProcedureCandidates>,
          ListSnowflakeStoredProceduresTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doDeleteJobEnvironment>, DeleteJobEnvironmentTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doDeleteServiceAccount>,
          DeleteSnowflakeServiceAccountUserTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doListJobRuns>, ListJobRunsTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doSetEnvironment>, SetJobEnvironmentTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doResetSetEnvironment>>
    | ReduxActionOutcome<ReturnType<typeof doSetJobDefinition>, SetJobDefinitionTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doResetSetJobDefinition>>
    | ReduxActionOutcome<ReturnType<typeof doCancelJobRun>, CancelJobRunTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doGetJobRunTimeline>, GetJobRunTimelineTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doPauseJobSchedule>, PauseJobTriggerTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doSetServiceAccount>,
          SetSnowflakeServiceAccountUserTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doResetSetServiceAccount>>
    | ReduxActionOutcome<ReturnType<typeof doResumeJobSchedule>, ResumeJobTriggerTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doTriggerJobRun>, TriggerJobRunTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doTriggerJobRuns>, TriggerJobRunsTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doListSnowflakeStages>, ListSnowflakeStagesTypeMap>
    | ReduxActionOutcome<
          ReturnType<typeof doGetSnowflakeStageDetails>,
          GetSnowflakeStageDetailsTypeMap
      >
    | ReduxActionOutcome<
          ReturnType<typeof doListSnowflakeFileFormats>,
          ListSnowflakeFileFormatsTypeMap
      >
    | ReduxActionOutcome<
          ReturnType<typeof doListSnowflakeStageFiles>,
          ListSnowflakeStageFilesTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doClearSnowflakeStageFiles>>
    | ReduxActionOutcome<
          ReturnType<typeof doGetSnowflakeStageFileInfo>,
          GetSnowflakeStageFileInfoTypeMap
      >
    | ReduxActionOutcome<
          ReturnType<typeof doGetModelTagCheckCandidates>,
          GetModelTagCheckCandidatesTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doClearModelTagCheckCandidates>>
    | ReduxActionOutcome<ReturnType<typeof doGetJobDefinition>, GetJobDefinitionTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doGetModelTagCheckCandidates>>
    | ReduxActionOutcome<
          ReturnType<typeof doGetModelTagSourceTables>,
          GetModelTagSourceTablesTypeMap
      >
    | ReduxActionOutcome<ReturnType<typeof doClearModelTagSourceTables>>
    | ReduxActionOutcome<ReturnType<typeof doGetLastJobRun>, GetLastJobRunTypeMap>
    | ReduxActionOutcome<ReturnType<typeof doSetJobTriggerCandidatesFilter>>
    | ReduxActionOutcome<ReturnType<typeof doSetJobDefinitionFilter>>;
